import { useEffect, useState } from 'react'
import type { Api } from '../api'
import { LOCAL, USD, inCurrency, type Currency, type CurrencyRate } from '../api/types'
import { Segmented, SearchSelect } from './ui'
import { currencyInfo } from './currency'
import { FlagBox, useFlags } from './CurrencyHover'

/**
 * Local | USD | a currency the reader picks — the one control, on every report.
 *
 * ---------------------------------------------------------------------------
 * WHY IT IS A COMPONENT AND NOT THREE COPIES
 *
 * The same rule the shared report chrome exists for: three pages each carrying
 * their own two-option Segmented is how Sales and Receivables ended up agreeing
 * and Material Planning did not. A reader who opens two reports must not have
 * to learn two currency controls.
 *
 * ---------------------------------------------------------------------------
 * THE PILL RELABELS TO THE CODE
 *
 * Before a choice the third pill says "Custom"; after one it says "AED". The
 * other two pills name their unit, so a third that went on saying "Custom"
 * would be the only control on screen that does not say what it is showing.
 * The picker stays visible the whole time that mode is active, because clicking
 * an already-selected radio does nothing — it is the only way back to change
 * AED to EUR.
 *
 * ---------------------------------------------------------------------------
 * AN EMPTY LIST HIDES THE OPTION, DELIBERATELY
 *
 * The server returns only currencies whose rate it will vouch for: D365's
 * Global rate from the dollar, offered only where D365 holds exactly one in
 * force today (045_currency_rate_live.sql). Anything else is listed in the
 * database and never offered. If that list comes
 * back empty — or the request fails — the third pill does not appear at all.
 * Offering a choice that would silently fall back to local is worse than not
 * offering it, because the reader would believe the figures had been restated.
 */
export function CurrencyControl({
  api,
  value,
  onChange,
  surface,
  label = 'Display currency',
}: {
  api: Api
  value: Currency
  onChange: (c: Currency) => void
  /** Developer-mode surface for the "?" affordance, where the page has one. */
  surface?: string
  label?: string
}) {
  const [rates, setRates] = useState<CurrencyRate[] | null>(null)
  // Shares the hover card's flag chunk, and its box, so the two never drift.
  const flags = useFlags()

  useEffect(() => {
    let cancelled = false
    api
      .currencyRates()
      .then((r) => !cancelled && setRates(r))
      // A FAILURE IS AN EMPTY LIST, not an error panel. This control sits in a
      // report header; losing the currency list must not take the report with
      // it, and the two modes that need no list keep working.
      .catch(() => !cancelled && setRates([]))
    return () => {
      cancelled = true
    }
  }, [api])

  const offerCustom = (rates?.length ?? 0) > 0
  const mode = value.mode

  const options: { value: 'local' | 'usd' | 'custom'; label: string; title?: string }[] = [
    { value: 'local', label: 'Local', title: 'As booked — a mixture of functional currencies' },
    { value: 'usd', label: 'USD', title: 'Converted to US dollars at source' },
  ]
  if (offerCustom) {
    options.push({
      value: 'custom',
      label: value.mode === 'custom' ? value.code : 'Custom',
      title:
        value.mode === 'custom'
          ? `Restated in ${value.code} at ${value.rate} per US dollar`
          : 'Restate the figures in one currency',
    })
  }

  const pick = (m: 'local' | 'usd' | 'custom') => {
    if (m === 'local') return onChange(LOCAL)
    if (m === 'usd') return onChange(USD)
    // Landing on Custom chooses the first offered currency rather than an empty
    // state: a pill that selects nothing looks broken, and the reader can
    // change it in the picker that has just appeared beside it.
    const first = rates?.[0]
    if (first) onChange(inCurrency(first.code, first.ratePerUsd))
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Segmented
        surface={surface}
        measureKey="currency"
        label={label}
        value={mode}
        onChange={pick}
        options={options}
      />
      {mode === 'custom' && rates && rates.length > 0 ? (
        <SearchSelect
          label="Currency"
          value={value.mode === 'custom' ? value.code : ''}
          buttonClassName="w-[7.5rem] py-1"
          /* The trigger is a flag and a code; the panel carries a name and a
             rate as well, so it is wider than the control that opens it.
             420 is measured, not guessed: it is what "United Arab Emirates
             Dirham" needs beside its flag, code and rate without truncating,
             and that is the longest name in the offered set. */
          panelWidth={420}
          onChange={(code) => {
            const r = rates.find((x) => x.code === code)
            if (r) onChange(inCurrency(r.code, r.ratePerUsd))
          }}
          /* FOUR COLUMNS, NOT ONE SENTENCE.
             Flag, code, name, rate — each in its own slot, so the codes line up
             down the left and the rates down the right and the eye can run
             either column. The first attempt packed all three into the hint,
             which took the whole row, truncated the code to "K." and put a
             horizontal scrollbar under the list.

             The name is the browser's own CLDR data, so a currency this
             platform has never shown still reads correctly the first time it
             appears. The rate is there because a reader is entitled to know
             what their figures were multiplied by. */
          options={rates.map((r) => {
            const info = currencyInfo(r.code)
            return {
              value: r.code,
              label: r.code,
              leading: <FlagBox region={info.region} flags={flags} />,
              hint: info.name ?? info.country ?? '',
              trailing: `${r.ratePerUsd} per USD`,
              // The note exists only where somebody had to decide the rate, so
              // it goes on the row's title rather than taking width from the
              // name on every other row.
              ...(r.note ? { title: r.note } : {}),
            }
          })}
        />
      ) : null}
    </div>
  )
}
